import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Mic, Check, AlertTriangle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

export default function AlexaAuth() {
  const { user }            = useAuth()
  const [linked, setLinked] = useState(false)
  const [error,  setError]  = useState('')

  // Alexa's redirect_uri/state are lost when we navigate to sign-in;
  // sessionStorage survives that redirect.
  useEffect(() => {
    const params      = new URLSearchParams(window.location.search)
    const redirectUri = params.get('redirect_uri')
    const state       = params.get('state')
    if (redirectUri) {
      sessionStorage.setItem('alexa_redirect_uri', redirectUri)
      sessionStorage.setItem('alexa_state', state || '')
    }
  }, [])

  // ── Get voice token ───────────────────────────────────────────────────────

  const { data: voiceToken, isLoading } = useQuery({
    queryKey: ['voice-token-alexa', user?.id],
    enabled:  !!user?.id,
    queryFn:  async () => {
      const { data: userData } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', user!.id)
        .single()
      if (!userData?.household_id) return null

      const { data: household } = await supabase
        .from('households')
        .select('voice_token')
        .eq('id', userData.household_id)
        .single()

      if (household?.voice_token) return household.voice_token

      const newToken = Array.from(
        crypto.getRandomValues(new Uint8Array(16))
      ).map(b => b.toString(16).padStart(2, '0')).join('')

      await supabase
        .from('households')
        .update({ voice_token: newToken })
        .eq('id', userData.household_id)

      return newToken
    },
  })

  // ── Redirect back to Alexa ────────────────────────────────────────────────

  useEffect(() => {
    if (!voiceToken) return

    const params      = new URLSearchParams(window.location.search)
    const redirectUri = params.get('redirect_uri') ||
                        sessionStorage.getItem('alexa_redirect_uri') || ''
    const state       = (params.get('state') ||
                        sessionStorage.getItem('alexa_state') || '').replace(/\s+/g, '')

    if (!redirectUri) {
      setLinked(true)
      return
    }

    try {
      const url     = new URL(redirectUri)
      const isValid = url.hostname.endsWith('amazon.com') ||
                      url.hostname.endsWith('amazon.co.jp') ||
                      url.hostname.endsWith('amazonalexa.com')
      if (!isValid) {
        setError('Invalid redirect URL: ' + url.hostname)
        return
      }
    } catch {
      setError('Could not parse redirect URL')
      return
    }

    sessionStorage.removeItem('alexa_redirect_uri')
    sessionStorage.removeItem('alexa_state')

    const finalUrl = `${redirectUri}#access_token=${voiceToken}&token_type=Bearer&state=${encodeURIComponent(state)}`
    window.location.href = finalUrl
    setLinked(true)
  }, [voiceToken])

  // ── Shared shell ──────────────────────────────────────────────────────────

  function Shell({ children }: { children: React.ReactNode }) {
    return (
      <main className="min-h-screen bg-cream flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-[380px] flex flex-col items-center text-center">
          {children}
        </div>
      </main>
    )
  }

  function MicTile({ tone = 'forest' }: { tone?: 'forest' | 'critical' }) {
    const bg   = tone === 'critical' ? 'bg-cream-warm' : 'bg-paper'
    const ring = tone === 'critical' ? 'border-terracotta/30' : 'border-border-sand'
    const fg   = tone === 'critical' ? 'text-terracotta' : 'text-forest'
    return (
      <div className={`h-16 w-16 rounded-2xl border ${ring} ${bg} flex items-center justify-center mb-6`}>
        <Mic className={`h-7 w-7 ${fg}`} strokeWidth={1.5} />
      </div>
    )
  }

  // ── Not signed in ─────────────────────────────────────────────────────────

  if (!user) {
    return (
      <Shell>
        <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-3">
          Voice
        </p>
        <MicTile />
        <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
          Sign in to link <em className="italic">Alexa</em>
        </h1>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] mb-6">
          Sign in to your Pantry account to connect it with Alexa.
        </p>
        <button
          type="button"
          onClick={() => {
            const fullPath = window.location.pathname + window.location.search
            window.location.href = `/sign-in?redirect=${encodeURIComponent(fullPath)}`
          }}
          className="w-full rounded-2xl bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em]"
        >
          Sign in to Pantry
        </button>
        <p className="font-serif italic text-[11px] text-ink-muted mt-4 leading-[1.5]">
          Your Alexa connection details are saved and will be used after sign-in.
        </p>
      </Shell>
    )
  }

  // ── Error ─────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <Shell>
        <p className="text-[10px] uppercase tracking-[0.18em] text-terracotta font-medium mb-3">
          Voice
        </p>
        <MicTile tone="critical" />
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-4 w-4 text-terracotta" strokeWidth={1.8} />
          <h1 className="font-serif text-[20px] text-terracotta leading-[1.2]">
            Something went <em className="italic">wrong</em>
          </h1>
        </div>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] mb-5">
          {error}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-2xl bg-forest text-forest-ink px-6 py-3 text-[13px] font-bold tracking-[-0.01em]"
        >
          Try again
        </button>
      </Shell>
    )
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Shell>
        <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-3">
          Voice
        </p>
        <MicTile />
        <span className="h-7 w-7 rounded-full border-2 border-forest border-t-transparent animate-spin mb-4" />
        <p className="font-serif italic text-[13px] text-ink-muted">
          Loading your account…
        </p>
      </Shell>
    )
  }

  // ── Linked ────────────────────────────────────────────────────────────────

  if (linked || voiceToken) {
    return (
      <Shell>
        <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-3">
          Voice
        </p>
        <div className="h-16 w-16 rounded-2xl bg-ok-bg border border-forest/30 flex items-center justify-center mb-6">
          <Check className="h-7 w-7 text-forest" strokeWidth={2} />
        </div>
        <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
          Pantry is <em className="italic">linked</em>
        </h1>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] mb-5">
          Redirecting you back to Alexa…
        </p>
        <div className="rounded-2xl bg-paper border border-border-sand px-4 py-3 w-full">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
            Try saying
          </p>
          <p className="font-serif italic text-[13px] text-ink leading-[1.4]">
            "Alexa, open Pantry Plus"
          </p>
        </div>
      </Shell>
    )
  }

  // ── Setting up (default) ──────────────────────────────────────────────────

  return (
    <Shell>
      <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-3">
        Voice
      </p>
      <MicTile />
      <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mb-2">
        Linking Pantry to <em className="italic">Alexa</em>
      </h1>
      <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5]">
        Setting up your connection…
      </p>
    </Shell>
  )
}
